import React, { useEffect, useState } from 'react';
import { Button, Modal, Input, Checkbox, Tooltip } from 'antd';
import {
  CopyOutlined,
  CheckOutlined,
  LockOutlined,
  QuestionCircleOutlined,
  DownOutlined,
  DeleteOutlined,
} from '@ant-design/icons';
import ApiKeysService from 'services/ApiKeysService/apiKeys.service';
import { useUserRole } from 'shared/hooks/useUserRole';
import { hasAdminAccess } from 'shared/utils/roleUtils';
import { ApiKeyListItem, ApiKeyCreatedData } from 'models/ApiKey/ApiKey.model';
import {
  ApiKeysPageProps,
  ApiKeysTableColumns,
  ApiKeysModalProps,
  ApiKeysHelpPanel,
  ApiKeyStatus,
} from 'enums/ApiKeysEnums';
import DashboardContainer from 'views/Dashboard/DashboardContainer';
import COButton from 'shared/components/COButton';
import COTable from 'shared/components/COTable';
import COAlertModal from 'shared/components/COAlertModal';
import coAlertModalStyles from 'shared/components/COAlertModal/COAlertModal.module.scss';
import userDetailsStyles from 'shared/components/Accounts/UserDetails/UserDetails.module.scss';
import { modalCloseIcon } from 'shared/components/OrganizationModal';
import { ModalProps } from 'enums/COAlertModal';
import { ReactComponent as AlertIcon } from 'assets/ColorIcons/SVG/group_58672.svg';
import { AddUserModalProps } from 'enums/AddUserModalProps';
import { ButtonSize } from 'enums/ButtonEnums';
import styles from './ApiKeys.module.scss';
import clsx from 'clsx';
import { ReactComponent as Add } from 'assets/icons/add.svg';
import { Link } from 'react-router-dom';
import { AppRoutes } from 'routes/routeConstants/appRoutes';

export interface ApiKeysProps {
  embedded?: boolean;
}

const ApiKeys: React.FC<ApiKeysProps> = ({ embedded = false }) => {
  const userRole = useUserRole();
  const isAdmin = hasAdminAccess(userRole);

  const {
    loading,
    buttonLoading,
    apiKeys,
    fetchApiKeys,
    createApiKey,
    deleteApiKey,
  } = ApiKeysService();

  const [createModalOpen, setCreateModalOpen] = useState(false);
  const [keyName, setKeyName] = useState('');
  const [keyNameError, setKeyNameError] = useState('');
  const [revealModalOpen, setRevealModalOpen] = useState(false);
  const [createdKey, setCreatedKey] = useState<ApiKeyCreatedData | null>(null);
  const [copied, setCopied] = useState(false);
  const [keySaved, setKeySaved] = useState(false);
  const [helpOpen, setHelpOpen] = useState(false);
  const [deleteKeyId, setDeleteKeyId] = useState<string | null>(null);

  useEffect(() => {
    if (isAdmin) {
      fetchApiKeys();
    }
  }, []);

  const handleOpenCreateModal = () => {
    setKeyName('');
    setKeyNameError('');
    setCreateModalOpen(true);
  };

  const handleCloseCreateModal = () => {
    setCreateModalOpen(false);
    setKeyName('');
    setKeyNameError('');
  };

  const handleCreateKey = async () => {
    const trimmed = keyName.trim();
    if (!trimmed) {
      setKeyNameError(ApiKeysModalProps.NAME_REQUIRED);
      return;
    }
    if (trimmed.length > 100) {
      setKeyNameError(ApiKeysModalProps.NAME_MAX_LENGTH);
      return;
    }
    setKeyNameError('');
    const result = await createApiKey(trimmed);
    if (result) {
      setCreateModalOpen(false);
      setCreatedKey(result);
      setCopied(false);
      setKeySaved(false);
      setRevealModalOpen(true);
    }
  };

  const handleCopyKey = async () => {
    if (createdKey?.api_key) {
      await navigator.clipboard.writeText(createdKey.api_key);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    }
  };

  const handleCloseRevealModal = () => {
    setRevealModalOpen(false);
    setCreatedKey(null);
  };

  const handleOpenDeleteModal = (record: ApiKeyListItem) => {
    setDeleteKeyId(record.id);
  };

  const handleDeleteModalCancel = () => {
    setDeleteKeyId(null);
  };

  const handleDeleteModalConfirm = async () => {
    if (!deleteKeyId) return;
    const ok = await deleteApiKey(deleteKeyId);
    if (ok) setDeleteKeyId(null);
  };

  const formatDate = (dateString: string | null) => {
    if (!dateString) return null;
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    });
  };

  const columns = [
    {
      title: ApiKeysTableColumns.NAME_TITLE,
      dataIndex: ApiKeysTableColumns.NAME_INDEX,
      key: ApiKeysTableColumns.NAME_INDEX,
      render: (name: string) => (
        <div>
          <strong>{name}</strong>
        </div>
      ),
    },
    {
      title: ApiKeysTableColumns.KEY_PREFIX_TITLE,
      dataIndex: ApiKeysTableColumns.KEY_PREFIX_INDEX,
      key: ApiKeysTableColumns.KEY_PREFIX_INDEX,
      render: (prefix: string) => (
        <div className={styles.keyPrefix}>{prefix}...</div>
      ),
    },
    {
      title: ApiKeysTableColumns.SCOPES_TITLE,
      dataIndex: ApiKeysTableColumns.SCOPES_INDEX,
      key: ApiKeysTableColumns.SCOPES_INDEX,
      render: (scopes: string[]) => (
        <div className={styles.scopeTagsRow}>
          {scopes?.map((scope: string) => (
            <div key={scope} className={styles.scopeTag}>
              {scope}
            </div>
          ))}
        </div>
      ),
    },
    {
      title: ApiKeysTableColumns.CREATED_TITLE,
      dataIndex: ApiKeysTableColumns.CREATED_INDEX,
      key: ApiKeysTableColumns.CREATED_INDEX,
      render: (date: string) => (
        <div className={styles.dateText}>{formatDate(date)}</div>
      ),
    },
    {
      title: ApiKeysTableColumns.LAST_USED_TITLE,
      dataIndex: ApiKeysTableColumns.LAST_USED_INDEX,
      key: ApiKeysTableColumns.LAST_USED_INDEX,
      render: (date: string | null) =>
        date ? (
          <div className={styles.dateText}>{formatDate(date)}</div>
        ) : (
          <div className={styles.neverUsedText}>Never</div>
        ),
    },
    {
      title: ApiKeysTableColumns.STATUS_TITLE,
      dataIndex: ApiKeysTableColumns.STATUS_INDEX,
      key: ApiKeysTableColumns.STATUS_INDEX,
      render: (revoked: boolean | undefined) =>
        revoked === true ? (
          <div className={styles.statusLegacyRevoked}>
            {ApiKeyStatus.LEGACY_REVOKED}
          </div>
        ) : (
          <div className={styles.statusActive}>{ApiKeyStatus.ACTIVE}</div>
        ),
    },
    {
      title: ApiKeysTableColumns.ACTION_TITLE,
      key: ApiKeysTableColumns.ACTION_INDEX,
      width: ApiKeysTableColumns.ACTION_WIDTH,
      align: 'right' as const,
      render: (_: unknown, record: ApiKeyListItem) =>
        record.revoked !== true && (
          <button
            type="button"
            className={styles.deleteKeyButton}
            onClick={e => {
              e.stopPropagation();
              handleOpenDeleteModal(record);
            }}
            disabled={buttonLoading}>
            <DeleteOutlined /> {ApiKeysModalProps.DELETE_KEY_OK}
          </button>
        ),
    },
  ];

  const wrapWithLayout = (node: React.ReactElement) =>
    embedded ? node : <DashboardContainer>{node}</DashboardContainer>;

  const pageClass = clsx(styles.pageContainer, embedded && styles.embedded);

  if (!isAdmin) {
    return wrapWithLayout(
      <div className={pageClass}>
        <div className={styles.accessDenied}>
          <LockOutlined className={styles.lockIcon} />
          <p>{ApiKeysPageProps.ACCESS_DENIED_MESSAGE}</p>
        </div>
      </div>
    );
  }

  return wrapWithLayout(
    <div className={pageClass}>
      <Modal
        destroyOnClose
        width={AddUserModalProps.MODAL_WIDTH}
        footer={null}
        wrapClassName={styles.createModalWrap}
        open={createModalOpen}
        closeIcon={modalCloseIcon}
        onCancel={handleCloseCreateModal}>
        <div className={styles.createModalForm}>
          <div className={styles.createModalHeader}>
            <h2>{ApiKeysModalProps.CREATE_TITLE}</h2>
          </div>
          <div className={styles.createModalField}>
            <label className={styles.createModalLabel} htmlFor="api-key-name">
              {ApiKeysModalProps.CREATE_NAME_LABEL}
            </label>
            <Input
              id="api-key-name"
              value={keyName}
              size="large"
              className={styles.createKeyNameInput}
              onChange={e => {
                setKeyName(e.target.value);
                if (keyNameError) setKeyNameError('');
              }}
              placeholder={ApiKeysModalProps.CREATE_NAME_PLACEHOLDER}
              maxLength={100}
              status={keyNameError ? 'error' : undefined}
            />
            {keyNameError && (
              <p className={styles.createModalError}>{keyNameError}</p>
            )}
          </div>
          <div className={styles.createModalButtons}>
            <COButton
              size={ButtonSize.SMALL}
              loading={buttonLoading}
              onClick={handleCreateKey}>
              {ApiKeysModalProps.CREATE_SUBMIT}
            </COButton>
            <COButton size={ButtonSize.SMALL} secondary onClick={handleCloseCreateModal}>
              {ApiKeysModalProps.CREATE_CANCEL}
            </COButton>
          </div>
        </div>
      </Modal>

      <Modal
        width={620}
        title={ModalProps.EMPTY_STRING}
        open={revealModalOpen}
        onCancel={handleCloseRevealModal}
        closable={false}
        maskClosable={false}
        keyboard={false}
        wrapClassName={clsx(
          coAlertModalStyles.modalContainer,
          styles.revealSuccessModalWrap
        )}
        footer={
          <div
            className={clsx(
              coAlertModalStyles.buttonContainer,
              coAlertModalStyles.paddingLeft,
              coAlertModalStyles.paddingRight
            )}>
            <COButton
              block
              disabled={!keySaved}
              onClick={handleCloseRevealModal}>
              {ApiKeysModalProps.REVEAL_DONE}
            </COButton>
          </div>
        }>
        <AlertIcon />
        <h2>{ApiKeysModalProps.REVEAL_TITLE}</h2>
        <div className={styles.revealModal}>
          <div className={styles.warningBannerWrap}>
            <div className={styles.warningBanner}>
              <p>{ApiKeysModalProps.REVEAL_WARNING}</p>
            </div>
          </div>

          <div className={styles.keyCodeBox}>
            <div className={styles.keyCodeHeader}>
              <div className={styles.keyCodeHeaderLeft}>
                <span className={styles.keyCodeLabel}>
                  {ApiKeysModalProps.REVEAL_KEY_LABEL}
                </span>
                {createdKey?.name ? (
                  <span className={styles.keyNameHint}>{createdKey.name}</span>
                ) : null}
              </div>
              <Tooltip
                title={
                  copied
                    ? ApiKeysModalProps.REVEAL_COPIED
                    : ApiKeysModalProps.REVEAL_COPY
                }
                open={copied ? true : undefined}
                placement="topRight">
                <Button
                  type="text"
                  className={styles.copyIconButton}
                  icon={copied ? <CheckOutlined /> : <CopyOutlined />}
                  onClick={handleCopyKey}
                  aria-label={
                    copied
                      ? ApiKeysModalProps.REVEAL_COPIED
                      : ApiKeysModalProps.REVEAL_COPY
                  }
                />
              </Tooltip>
            </div>
            <div className={styles.keyValueScroll}>
              <code className={styles.keyValue}>{createdKey?.api_key}</code>
            </div>
          </div>

          <Checkbox
            checked={keySaved}
            onChange={e => setKeySaved(e.target.checked)}
            className={styles.confirmCheckbox}>
            {ApiKeysModalProps.REVEAL_CONFIRM}
          </Checkbox>
        </div>
      </Modal>

      <COAlertModal
        showModalState={deleteKeyId !== null}
        handleModalCancel={handleDeleteModalCancel}
        handleModalConfirm={handleDeleteModalConfirm}
        deleteModal
        heading={ApiKeysModalProps.DELETE_KEY_TITLE}
        subHeading={ApiKeysModalProps.DELETE_KEY_CONFIRM}
        confirmLabel={ApiKeysModalProps.DELETE_KEY_OK}
        cancelLabel={ApiKeysModalProps.DELETE_KEY_CANCEL}
      />

      <div style={{ float: 'right', marginTop: '-4.5rem' }}>
        <COButton onClick={handleOpenCreateModal}>
          <Add /> {ApiKeysPageProps.CREATE_BUTTON}
        </COButton>
      </div>
      <div className={userDetailsStyles.rowOne}>
        <div className={userDetailsStyles.searchContainer} />
      </div>
      <COTable<ApiKeyListItem>
        loading={loading}
        columns={columns}
        data={apiKeys}
        rowKey="id"
      />

      <div className={styles.helpPanel}>
        <div
          className={styles.helpHeader}
          onClick={() => setHelpOpen(!helpOpen)}>
          <QuestionCircleOutlined className={styles.helpIcon} />
          <h4>{ApiKeysHelpPanel.TITLE}</h4>
          <DownOutlined
            className={`${styles.chevron} ${helpOpen ? styles.open : ''}`}
          />
        </div>
        {helpOpen && (
          <div className={styles.helpContent}>
            <ul>
              <li>
                <Link
                  to={AppRoutes.API_REFERENCE}
                  target="_blank"
                  rel="noopener noreferrer">
                  {ApiKeysHelpPanel.FULL_API_REFERENCE}
                </Link>
              </li>
              <li>
                Keys are Bearer tokens:{' '}
                <code>Authorization: Bearer &lt;API_KEY&gt;</code>
              </li>
              <li>{ApiKeysHelpPanel.COMPANY_SCOPE}</li>
              <li>{ApiKeysHelpPanel.READ_ONLY}</li>
              <li>{ApiKeysHelpPanel.ADMIN_ONLY}</li>
            </ul>
          </div>
        )}
      </div>
    </div>
  );
};

export default ApiKeys;
